// LeadPipelineSnapshotReport.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  buildLeadPipelineSnapshot,
  formatPipelineDays,
  formatPipelinePercent,
} from 'collection/reports/lead/leadPipelineSnapshot';

// CSS
import './LeadPipelineSnapshotReport.css';

/**
 * LeadPipelineSnapshotReport
 *
 * Overview widget: current Open / Won / Lost mix + open aging.
 *
 * @returns {ReactNode}
 */
const LeadPipelineSnapshotReport = ({
  nodes = [],
  isLoading = false,
  title = 'Pipeline',
  description = 'Open vs closed mix for leads in this collection.',
}) => {
  const { theme } = useTheme();

  const report = useMemo(
    () => buildLeadPipelineSnapshot({ nodes }),
    [nodes],
  );

  if (isLoading && !report?.leadCount) {
    return (
      <div className="lead-pipeline-report-container">
        <div
          className="lead-pipeline-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          Loading…
        </div>
      </div>
    );
  }

  if (!report || report.leadCount === 0) {
    return (
      <div className="lead-pipeline-report-container">
        <div className="lead-pipeline-report-intro">
          <div
            className="lead-pipeline-report-title"
            style={{ color: theme.foreground }}
          >
            {title}
          </div>
        </div>
        <div
          className="lead-pipeline-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          No leads yet.
        </div>
      </div>
    );
  }

  const mixSegments = [
    { key: 'open', count: report.openCount, ratio: report.percentOpen, className: 'open' },
    { key: 'won', count: report.wonCount, ratio: report.percentWon, className: 'won' },
    { key: 'lost', count: report.lostCount, ratio: report.percentLost, className: 'lost' },
  ].filter((segment) => segment.count > 0);

  return (
    <div className="lead-pipeline-report-container">
      <div className="lead-pipeline-report-intro">
        <div
          className="lead-pipeline-report-title"
          style={{ color: theme.foreground }}
        >
          {title}
        </div>
        <div
          className="lead-pipeline-report-description"
          style={{ color: theme.foregroundMuted }}
        >
          {`${report.leadCount} ${report.leadCount === 1 ? 'lead' : 'leads'}`}
        </div>
      </div>

      <div className="lead-pipeline-report-summary">
        <div className="lead-pipeline-report-metric">
          <div
            className="lead-pipeline-report-metric-label"
            style={{ color: theme.foregroundMuted }}
          >
            Open
          </div>
          <div
            className="lead-pipeline-report-metric-value"
            style={{ color: theme.foreground }}
          >
            {report.openCount}
          </div>
          <div
            className="lead-pipeline-report-metric-note"
            style={{ color: theme.foregroundMuted }}
          >
            {formatPipelinePercent(report.percentOpen)}
          </div>
        </div>
        <div className="lead-pipeline-report-metric">
          <div
            className="lead-pipeline-report-metric-label"
            style={{ color: theme.foregroundMuted }}
          >
            Won
          </div>
          <div
            className="lead-pipeline-report-metric-value"
            style={{ color: theme.foreground }}
          >
            {report.wonCount}
          </div>
          <div
            className="lead-pipeline-report-metric-note"
            style={{ color: theme.foregroundMuted }}
          >
            {formatPipelinePercent(report.percentWon)}
          </div>
        </div>
        <div className="lead-pipeline-report-metric">
          <div
            className="lead-pipeline-report-metric-label"
            style={{ color: theme.foregroundMuted }}
          >
            Lost
          </div>
          <div
            className="lead-pipeline-report-metric-value"
            style={{ color: theme.foreground }}
          >
            {report.lostCount}
          </div>
          <div
            className="lead-pipeline-report-metric-note"
            style={{ color: theme.foregroundMuted }}
          >
            {formatPipelinePercent(report.percentLost)}
          </div>
        </div>
      </div>

      {mixSegments.length > 0 ? (
        <div className="lead-pipeline-report-mix">
          <div className="lead-pipeline-report-mix-bar" aria-hidden="true">
            {mixSegments.map((segment) => (
              <div
                className={`lead-pipeline-report-mix-segment lead-pipeline-report-mix-segment-${segment.className}`}
                key={segment.key}
                style={{ flexGrow: segment.count, flexBasis: 0 }}
              />
            ))}
          </div>
          <div className="lead-pipeline-report-mix-legend">
            {mixSegments.map((segment) => (
              <div
                className="lead-pipeline-report-mix-legend-item"
                key={segment.key}
                style={{ color: theme.foregroundMuted }}
              >
                <span
                  className={`lead-pipeline-report-mix-swatch lead-pipeline-report-mix-swatch-${segment.className}`}
                />
                <span>
                  {`${segment.key.charAt(0).toUpperCase()}${segment.key.slice(1)} ${formatPipelinePercent(segment.ratio)}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div
        className="lead-pipeline-report-aging"
        style={{ color: theme.foregroundMuted }}
      >
        {report.openCount === 0
          ? 'No open leads.'
          : `${report.agingOpenCount} open ${report.agingOpenCount === 1 ? 'lead is' : 'leads are'} ${report.agingDays}+ days old · avg open age ${formatPipelineDays(report.avgOpenAgeDays)}`}
      </div>
    </div>
  );
};

LeadPipelineSnapshotReport.displayName = 'LeadPipelineSnapshotReport';

export default LeadPipelineSnapshotReport;
